import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { NonNullableFormBuilder } from '@angular/forms';
import {
  ConsentTypes,
  personalFormRules,
  sharedFormValidator,
} from '@credit-acceptance/shareable-utilities';
import { adobeAnalytics } from 'adobe-analytics';
import {
  ConsentTextInfoQueryService,
  CreateAbandonEmailMutationService,
  dateOfBirth,
} from 'graphql-client';
import { isEqual } from 'lodash';
import { map, takeUntil, tap, zip } from 'rxjs';

import { error_sign, GET_PREQUALIFIED_JSON_URL } from '../../assets/constants';
import { AWSService } from '../services/aws.service';
import { Unsubscribe } from '../shared/classes/unsubscribe.class';
import { CrossComponentDateValidationService } from '../validators/cross-component-date-validation.service';
import { SiblingValidationService } from '../validators/sibling-validation.service';

@Component({
  selector: 'capq-personal',
  templateUrl: './personal.component.html',
  styleUrls: ['./personal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PersonalComponent
  extends Unsubscribe
  implements OnInit, OnChanges, AfterViewInit
{
  @Output() activeStep: EventEmitter<number> = new EventEmitter<number>();
  @Input() adobeScriptLoaded = false;
  @ViewChild('firstNameInput')
  firstNameInput!: ElementRef;

  emailDisclosure = '';
  emailDisclosureVersion = 0;

  initState = {
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    email: '',
    emailConfirmation: '',
    phone: '',
  };

  formStartEventEmitted = false;

  personalForm = this.fb.group(this.initState);

  exclamation = error_sign;
  dateOfBirth18YearsOfAgeFlag = false;
  formFieldData: any = {};
  underAgeError = false;
  zipCodeErrorFlag = false;
  url = GET_PREQUALIFIED_JSON_URL;
  globalText: any;
  formIsIncomplete = true;
  nameMaxLength = 30;
  emailMaxLength = 240;
  emailsAreValidAndMatching = true;
  elementInFocus = 'firstName';
  pageViewFired = false;
  isWaiting = false;

  constructor(
    private readonly fb: NonNullableFormBuilder,
    private readonly awsService: AWSService,
    private readonly createAbandonEmailMutationService: CreateAbandonEmailMutationService,
    private readonly consentTextInfoQueryService: ConsentTextInfoQueryService,
    private cdRef: ChangeDetectorRef,
    private siblingValidationService: SiblingValidationService,
    private crossComponentDateValidation: CrossComponentDateValidationService
  ) {
    super();
  }
  ngOnInit(): void {
    this.siblingValidationService.validate$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        const formKeys = Object.keys(this.personalForm.controls);
        for (const key of formKeys) {
          this.personalForm.controls[
            key as keyof typeof personalFormRules
          ].markAsTouched();
          this.validate(
            this.personalForm.controls[key as keyof typeof personalFormRules]
              .value,
            key as keyof typeof personalFormRules
          );
        }
        this.cdRef.detectChanges();
        this.siblingValidationService.reportValidation(
          1,
          this.personalForm.getRawValue(),
          this.personalForm.valid
        );
      });
    zip(
      this.awsService.prequalifiedJSONData$.pipe(
        tap((data) => {
          this.globalText = data;
          this.formFieldData = data.personal;
        })
      ),
      this.consentTextInfoQueryService
        .fetch({
          consentType: ConsentTypes.EMAIL_DISCLOSURE,
        })
        .pipe(map((result) => result.data.getConsentByType))
    )
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(([_, emailDisclosureInfo]) => {
        this.initForm();
        if (emailDisclosureInfo) {
          this.emailDisclosure = emailDisclosureInfo.consenttext;
          this.emailDisclosureVersion = emailDisclosureInfo.consenttextid;
          // Manually triggering change detection to display the disclosure on page load.
          this.cdRef.detectChanges();
        }
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes?.adobeScriptLoaded?.currentValue) {
      adobeAnalytics.emitEvent('cac-page-view', 'personal', {
        form_design: 'v 1.0',
        section_name: 'personal',
        form_submission_id: '',
        dealer_list: '',
        form_submission_status: '',
        event_type: 'cac-page-view',
      });
      this.pageViewFired = true;
    }
  }

  ngAfterViewInit() {
    // Adding this as additional way to set focus as testing using autofocus on element is not working
    this.firstNameInput.nativeElement.focus();
  }

  initForm(): void {
    this.personalForm.valueChanges
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        if (
          this.adobeScriptLoaded &&
          !isEqual(this.personalForm.getRawValue(), this.initState) &&
          !this.formStartEventEmitted
        ) {
          this.formStartEventEmitted = true;
          adobeAnalytics.emitFormStart('personal');
        }
      });

    this.personalForm.controls.firstName.valueChanges
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((value: string) => {
        this.personalForm.controls.firstName.setValue(
          this.capitalizeFirstChar(value),
          {
            emitEvent: false,
          }
        );
      });
    this.personalForm.controls.lastName.valueChanges
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((value: string) => {
        this.personalForm.controls.lastName.setValue(
          this.capitalizeFirstChar(value),
          {
            emitEvent: false,
          }
        );
      });
    this.personalForm.controls.dateOfBirth.valueChanges
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((value) => {
        dateOfBirth(value);
      });
    this.personalForm.valueChanges
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        const entries = Object.entries(this.personalForm.value) as [
          keyof typeof personalFormRules,
          string
        ][];
        const missingValue = entries.find(
          ([key, value]) => !value && personalFormRules[key].required
        );
        if (missingValue) {
          this.formIsIncomplete = true;
        } else {
          for (const [key, value] of entries) {
            this.validate(value, key, true);
          }
        }
      });
  }

  get f() {
    return this.personalForm.controls;
  }

  removeTrailingSpaces(
    event: any,
    formControlName: keyof typeof this.personalForm.controls
  ) {
    this.personalForm.controls[formControlName].setValue(
      event.target.value.trim()
    );
  }

  // used to control output of the input length / max number of chars
  onFocus(key: keyof typeof personalFormRules) {
    this.elementInFocus = key;
  }

  // extract value from focus event and pass into validation method
  parseInputAndValidate(
    event: FocusEvent,
    key: keyof typeof personalFormRules
  ) {
    const { value } = event.target as HTMLInputElement;
    this.validate(value, key);
  }

  // runs validation of each field based on the config provided in shareable-utilities
  validate(
    value: string,
    key: keyof typeof personalFormRules,
    softCheck?: boolean
  ) {
    const valueToValidate = this.sanitizePhoneNumber(value, key);
    const { errors } = sharedFormValidator(
      'personalFormRules',
      key,
      valueToValidate
    );
    if (errors && errors.length) {
      const errorObject: { [key: string]: string } = {};
      for (const err of errors) {
        errorObject[err.rule] = err.message;
      }
      softCheck
        ? (this.formIsIncomplete = true)
        : this.personalForm.controls[key].setErrors(errorObject);
    } else {
      softCheck
        ? (this.formIsIncomplete = false)
        : this.personalForm.controls[key].setErrors(null);
      if (key === 'dateOfBirth') {
        this.crossComponentDateValidation.validateDownstreamDates();
      }
    }
  }

  sanitizePhoneNumber(value: string, key: keyof typeof personalFormRules) {
    return key === 'phone' ? value.replace(/-/g, '') : value;
  }

  // compares emails when both values are present and none of them has other validation errors
  emailConfirmationValidator = () => {
    const emailControl = this.personalForm.controls.email;
    const email = emailControl.value?.toLowerCase();
    const emailErrors = emailControl.errors;
    const emailConfirmControl = this.personalForm.controls.emailConfirmation;
    const emailConfirm = emailConfirmControl.value?.toLowerCase();
    const emailConfirmControlErrors = emailConfirmControl.errors;

    if (
      !emailErrors &&
      !emailConfirmControlErrors &&
      email &&
      emailConfirm &&
      email === emailConfirm
    ) {
      return true;
    } else {
      return false;
    }
  };

  compareEmails() {
    const { value: emailValue } = this.personalForm.controls.email;
    const { value: confirmationEmailValue } =
      this.personalForm.controls.emailConfirmation;
    if (emailValue && confirmationEmailValue) {
      if (this.emailConfirmationValidator()) {
        this.emailsAreValidAndMatching = true;
        this.createAbandonEmailMutationService
          .mutate({
            createAbandonEmailInput: {
              email: emailValue,
              firstName: this.personalForm.controls.firstName.value,
              lastName: this.personalForm.controls.lastName.value,
              mobilePhone: this.personalForm.controls.phone.value,
            },
          })
          .pipe(takeUntil(this.unsubscribe$))
          .subscribe();
      } else {
        this.emailsAreValidAndMatching = false;
      }
    }
  }

  // additional guard to make sure network request contain proper name formatting
  capitalizeFirstChar(str: string) {
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  }

  titleCase(str: string) {
    return str
      ?.replace(/\b\S/g, (t) => t.toUpperCase())
      .replace(/[^a-zA-Z ]*/g, '');
  }
}
